// src/components/Dashboard.jsx
import React, { useEffect, useState } from "react";
import axiosInstance from "../utills/axiosInstance";

export default function Dashboard() {
  const [stats, setStats] = useState({
    numCustomers: 0,
    numSales: 0,
    valueSales: 0,
    pendingCustomers: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get the email that the backend expects
  const email = localStorage.getItem("userEmail");

  useEffect(() => {
    const fetchStats = async () => {
      if (!email) {
        setError("You are not logged in.");
        setLoading(false);
        return;
      }

      try {
        // Use the same axiosInstance (adds token + baseURL)
        const res = await axiosInstance.get(
          `/api/customers?email=${encodeURIComponent(email)}`
        );

        const customers = res.data;

        const completed = customers.filter((c) => c.inspection === "Completed");
        const numSales = completed.length;

        // Sum saleAmount if it exists, otherwise 0
        const valueSales = completed.reduce(
          (sum, c) => sum + (Number(c.saleAmount) || 0),
          0
        );

        const pendingCustomers = customers.filter(
          (c) => c.inspection === "Pending"
        ).length;

        setStats({
          numCustomers: customers.length,
          numSales,
          valueSales,
          pendingCustomers,
        });
      } catch (err) {
        console.error("Dashboard fetch error:", err);
        setError(
          err.response?.data?.error ||
            err.message ||
            "Failed to load dashboard data."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [email]);

  // Loading UI
  if (loading) {
    return (
      <div className="loading">
        <style jsx>{`
          .loading {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            height: calc(100vh - 64px);
            font-size: 1.2rem;
            color: #666;
          }
          .spinner {
            border: 4px solid #f3f3f3;
            border-top: 4px solid #2e86de;
            border-radius: 50%;
            width: 40px;
            height: 40px;
            animation: spin 1s linear infinite;
            margin-bottom: 1rem;
          }
          @keyframes spin {
            to {
              transform: rotate(360deg);
            }
          }
        `}</style>
        <div className="spinner" />
        Loading dashboard...
      </div>
    );
  }

  // Error UI
  if (error) {
    return (
      <div className="error">
        <style jsx>{`
          .error {
            text-align: center;
            margin-top: 3rem;
            padding: 2rem;
            background: #fdf2f2;
            border: 1px solid #f5c6cb;
            border-radius: 10px;
            color: #721c24;
            max-width: 500px;
            margin-left: auto;
            margin-right: auto;
          }
          .retry {
            margin-top: 1rem;
            background: #2e86de;
            color: white;
            border: none;
            padding: 0.6rem 1.2rem;
            border-radius: 5px;
            cursor: pointer;
          }
        `}</style>
        <h3>Failed to load dashboard</h3>
        <p>{error}</p>
        <button className="retry" onClick={() => window.location.reload()}>
          Retry
        </button>
      </div>
    );
  }

  // Main Dashboard UI
  return (
    <>
      <style jsx>{`
        .page {
          padding: 2rem;
          font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
          background-color: #f4f6f8;
          min-height: calc(100vh - 64px);
        }
        .title {
          text-align: center;
          margin-bottom: 2rem;
          font-size: 2rem;
          color: #333;
          letter-spacing: 1px;
        }
        .grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1.5rem;
        }
        .card {
          background: #fff;
          border-radius: 10px;
          padding: 1.5rem;
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
          text-align: center;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
        }
        .cardTitle {
          margin-bottom: 1rem;
          font-size: 1.2rem;
          color: #555;
          font-weight: 500;
        }
        .value {
          font-size: 2rem;
          font-weight: bold;
          color: #2e86de;
        }
        .pending {
          border-left: 5px solid orange;
        }
        .pending .value {
          color: orange;
        }
      `}</style>

      <div className="page">
        <h2 className="title">Dashboard</h2>

        <div className="grid">
          <div className="card">
            <h3 className="cardTitle">No of Customers</h3>
            <p className="value">{stats.numCustomers}</p>
          </div>

          <div className="card">
            <h3 className="cardTitle">No of Sales</h3>
            <p className="value">{stats.numSales}</p>
          </div>

          <div className="card">
            <h3 className="cardTitle">Value of Sales</h3>
            <p className="value">₹{stats.valueSales.toLocaleString()}</p>
          </div>

          <div className="card pending">
            <h3 className="cardTitle">Pending Customers</h3>
            <p className="value">{stats.pendingCustomers}</p>
          </div>
        </div>
      </div>
    </>
  );
}